import { TIER_FEATURE_CATALOG } from '@common/enums';
import type { AccountTier } from '@common/types';

function featureAccess(enabledKeys: ReadonlyArray<string>): Record<string, boolean> {
  const enabled = new Set(enabledKeys);
  const access: Record<string, boolean> = {};
  for (const group of TIER_FEATURE_CATALOG) {
    for (const feature of group.features) {
      access[feature.key] = enabled.has(feature.key);
    }
  }
  return access;
}

const LITE_FEATURES = ['guests', 'reviews', 'reports'];

const CORE_FEATURES = [
  ...LITE_FEATURES,
  'communications',
  // The legacy LITE package sends only the daily summary and weekly report.
  'email-alerts',
  'service-tickets',
  'departments',
  'sentiment',
  'custom-dashboards',
  'social-sharing',
];

const PRO_FEATURES = [
  ...CORE_FEATURES,
  'milestones',
  'ai-analytics',
  'social-monitoring',
];

export const ACCOUNT_TIERS: ReadonlyArray<AccountTier> = [
  {
    key: 'lite',
    name: 'Lite',
    tagline: 'The essentials to collect and act on guest feedback.',
    description: 'Core review management and reporting for a single property getting started.',
    monthlyPrice: 49,
    currency: 'USD',
    featureAccess: featureAccess(LITE_FEATURES),
    limits: {
      seats: 5,
      'monthly-reviews': 500,
      'ai-credits': 0,
      'connected-sources': 3,
      'data-retention': 6,
    },
  },
  {
    key: 'core',
    name: 'Core',
    tagline: 'Operations and intelligence for a growing guest experience team.',
    description: 'Adds communications, service tickets, sentiment, and custom dashboards.',
    monthlyPrice: 149,
    currency: 'USD',
    highlighted: true,
    featureAccess: featureAccess(CORE_FEATURES),
    limits: {
      seats: 25,
      'monthly-reviews': 5000,
      'ai-credits': 2000,
      'connected-sources': 10,
      'data-retention': 24,
    },
  },
  {
    key: 'pro',
    name: 'Pro',
    tagline: 'The full platform with AI analytics and unlimited scale.',
    description: 'Everything in Core plus AI analytics, social monitoring, and unlimited usage.',
    monthlyPrice: 399,
    currency: 'USD',
    featureAccess: featureAccess(PRO_FEATURES),
    limits: {
      seats: 'unlimited',
      'monthly-reviews': 'unlimited',
      'ai-credits': 'unlimited',
      'connected-sources': 'unlimited',
      'data-retention': 'unlimited',
    },
  },
];
